import type { AssistantResponse, PageContext } from './types';

/** 模擬網路延遲 */
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/** 簡單關鍵字比對（不分大小寫） */
function contains(text: string, ...keywords: string[]): boolean {
  const lower = text.toLowerCase();
  return keywords.some(k => lower.includes(k.toLowerCase()));
}

export async function getMockResponse(
  userInput: string,
  context: PageContext
): Promise<AssistantResponse> {
  await delay(700);

  // ── 跨頁面通用指令 ──────────────────────────────────────────────────────────

  // 導航到各頁面
  if (contains(userInput, '設定', '設置', 'settings') && contains(userInput, '去', '帶我', '跳到', '到', '開啟', '打開')) {
    return {
      text: '好的，幫您切換到系統設定頁面。',
      actions: [{ type: 'navigate', tab: 'settings', description: '切換到系統設定' }],
    };
  }
  if (contains(userInput, '快速生成', '生成頁', 'quick') && contains(userInput, '去', '帶我', '跳到', '到', '開啟')) {
    return {
      text: '好的，幫您切換到里務工具包（AI 快速生成）頁面。',
      actions: [
        { type: 'navigate', tab: 'toolkit', description: '切換到工具包/快速生成' },
      ],
    };
  }
  if (contains(userInput, '圖片庫', '生成圖', 'gallery') && contains(userInput, '去', '帶我', '跳到', '到', '開啟', '查看', '看')) {
    return {
      text: '好的，幫您切換到圖片庫頁面。',
      actions: [{ type: 'navigate', tab: 'gallery', description: '切換到圖片庫' }],
    };
  }
  if (contains(userInput, '印章', 'stamp') && contains(userInput, '去', '帶我', '跳到', '到', '開啟', '管理')) {
    return {
      text: '好的，幫您切換到印章管理頁面。',
      actions: [{ type: 'navigate', tab: 'stamp-manager', description: '切換到印章管理' }],
    };
  }
  if (contains(userInput, '風格轉移', 'style', '風格生成', '人像') && contains(userInput, '去', '帶我', '跳到', '到', '開啟')) {
    return {
      text: '好的，幫您切換到風格轉移頁面。',
      actions: [{ type: 'navigate', tab: 'style-transfer', description: '切換到風格轉移' }],
    };
  }
  if (contains(userInput, '工具包', 'toolkit') && contains(userInput, '去', '帶我', '跳到', '到', '開啟')) {
    return {
      text: '好的，幫您切換到里務工具包頁面。',
      actions: [{ type: 'navigate', tab: 'toolkit', description: '切換到工具包' }],
    };
  }
  if (contains(userInput, '紀錄', '歷史', 'history') && contains(userInput, '去', '帶我', '跳到', '到', '開啟', '查看', '看')) {
    return {
      text: '好的，幫您切換到圖片庫頁面，所有歷史生成圖片都在這裡。',
      actions: [{ type: 'navigate', tab: 'gallery', description: '切換到圖片庫' }],
    };
  }

  // 查詢目前設定
  if (contains(userInput, '里名', '里別', '村名', '組織', '單位', '稱謂', '姓名', '名字', '里是什麼', '設定是什麼', '目前設定')) {
    const { organizationName, organizationType, name, title } = context.globalState;
    const parts = [
      `組織類型：${organizationType}`,
      organizationName ? `組織名稱：${organizationName}` : null,
      `姓名：${name}`,
      title ? `稱謂：${title}` : null,
    ].filter(Boolean);
    return {
      text: `目前的設定為 ${parts.join('，')}。`,
      actions: [],
    };
  }

  // 查詢所在頁面
  if (contains(userInput, '我在哪', '現在在哪', '哪個頁面', '目前頁面')) {
    return {
      text: `您目前在「${context.pageLabel}」頁面。`,
      actions: [],
    };
  }

  // 功能說明
  if (contains(userInput, '功能', '有什麼', '介紹', '說明') && contains(userInput, '功能', '頁面', '系統', '介紹')) {
    return {
      text: `這個系統有以下主要功能：\n\n• **里務工具包**：選擇範本後進入 AI 快速生成流程\n• **人像風格生成**：將您的圖片轉換成動漫、水彩等藝術風格\n• **圖片庫**：瀏覽並下載所有歷史生成圖片\n• **印章管理**：管理自訂印章素材，內建去背工具\n• **系統設定**：設定組織名稱、組織類型、姓名、稱謂與形象照\n\n需要我幫您去哪個頁面嗎？`,
      actions: [],
    };
  }

  // ── 快速生成頁 ─────────────────────────────────────────────────────────────

  if (context.pageId === 'toolkit' || contains(userInput, '快速生成', '生成文宣')) {

    // 選規格 / ratio
    if (contains(userInput, 'A4', 'a4')) {
      const isLandscape = contains(userInput, '橫式', '橫向', 'landscape');
      const ratio = isLandscape ? 'A4-landscape' : 'A4';
      return {
        text: `已幫您選擇 ${isLandscape ? 'A4 橫式' : 'A4 直式'} 規格。`,
        actions: [
          { type: 'set-gen-config', payload: { answers: { imageAspectRatio: ratio } }, description: `選擇 ${ratio} 規格` },
        ],
      };
    }
    if (contains(userInput, 'IG', 'instagram', '方形', '1:1')) {
      return {
        text: '已幫您選擇 IG 方形 (1:1) 規格。',
        actions: [
          { type: 'set-gen-config', payload: { answers: { imageAspectRatio: 'IG' } }, description: '選擇 IG 規格' },
        ],
      };
    }
    if (contains(userInput, 'FB', 'facebook', '橫幅')) {
      return {
        text: '已幫您選擇 Facebook 橫幅規格。',
        actions: [
          { type: 'set-gen-config', payload: { answers: { imageAspectRatio: 'FB' } }, description: '選擇 FB 規格' },
        ],
      };
    }

    // 選語氣 / tone
    if (contains(userInput, '親切', '友善', '溫馨', 'friendly')) {
      return {
        text: '已幫您選擇「親切友善」語氣。',
        actions: [
          { type: 'set-gen-config', payload: { answers: { textToneInImage: 'friendly' } }, description: '選擇親切語氣' },
        ],
      };
    }
    if (contains(userInput, '專業', '正式', 'professional')) {
      return {
        text: '已幫您選擇「專業正式」語氣。',
        actions: [
          { type: 'set-gen-config', payload: { answers: { textToneInImage: 'professional' } }, description: '選擇專業語氣' },
        ],
      };
    }
    if (contains(userInput, '活力', '活潑', '熱情', 'energetic')) {
      return {
        text: '已幫您選擇「活力熱情」語氣。',
        actions: [
          { type: 'set-gen-config', payload: { answers: { textToneInImage: 'energetic' } }, description: '選擇活力語氣' },
        ],
      };
    }
    if (contains(userInput, '幽默', '輕鬆', '有趣', 'humorous')) {
      return {
        text: '已幫您選擇「幽默輕鬆」語氣。',
        actions: [
          { type: 'set-gen-config', payload: { answers: { textToneInImage: 'humorous' } }, description: '選擇幽默語氣' },
        ],
      };
    }

    // 選風格
    if (contains(userInput, '現代', '簡約', 'modern')) {
      return {
        text: '已幫您選擇「現代簡約」視覺風格。',
        actions: [
          { type: 'set-gen-config', payload: { answers: { imageStyle: 'modern' } }, description: '選擇現代風格' },
        ],
      };
    }
    if (contains(userInput, '傳統', '復古', 'traditional')) {
      return {
        text: '已幫您選擇「傳統復古」視覺風格。',
        actions: [
          { type: 'set-gen-config', payload: { answers: { imageStyle: 'traditional' } }, description: '選擇傳統風格' },
        ],
      };
    }

    // 填入內容
    if (contains(userInput, '健康講座', '健檢', '醫療')) {
      return {
        text: '已幫您填入健康講座的活動內容，請確認後可繼續下一步。',
        actions: [
          { type: 'set-gen-step', step: 2, description: '切換到步驟 2（填寫內容）' },
          { type: 'set-gen-config', payload: { answers: { contentInImage: '本里將於本週日上午 9 時舉辦免費里民健康講座，邀請專業醫師主講慢性病預防與日常保健知識，歡迎里民踴躍參加，現場提供免費血壓、血糖量測服務。' } }, description: '填入健康講座內容' },
        ],
      };
    }
    if (contains(userInput, '中秋', '烤肉', '月餅')) {
      return {
        text: '已幫您填入中秋節活動的內容，請確認後可繼續下一步。',
        actions: [
          { type: 'set-gen-step', step: 2, description: '切換到步驟 2（填寫內容）' },
          { type: 'set-gen-config', payload: { answers: { contentInImage: '中秋佳節將至，本里舉辦「月圓里民同樂晚會」，誠邀全體里民攜家帶眷共襄盛舉。活動當天備有烤肉區、才藝表演及抽獎活動，歡迎踴躍報名參加。' } }, description: '填入中秋節活動內容' },
        ],
      };
    }
    if (contains(userInput, '清潔', '環境', '打掃', '清理')) {
      return {
        text: '已幫您填入社區環境清潔活動的內容。',
        actions: [
          { type: 'set-gen-step', step: 2, description: '切換到步驟 2（填寫內容）' },
          { type: 'set-gen-config', payload: { answers: { contentInImage: '為維護社區整潔美觀，本里將於週六早上 8 時舉辦社區大掃除活動，請里民自備手套、掃把等清潔工具，一同打造乾淨舒適的居住環境。' } }, description: '填入清潔活動內容' },
        ],
      };
    }
    if (contains(userInput, '填', '輸入', '寫', '幫我填') && contains(userInput, '內容', '文案', '文字')) {
      return {
        text: '請告訴我活動的主題或類型（例如：健康講座、中秋活動、社區清潔），我可以幫您產生對應的活動描述內容！',
        actions: [],
      };
    }

    // 切換步驟
    if (contains(userInput, '步驟一', '第一步', '步驟1', '回到第一步', '規格')) {
      return {
        text: '已幫您切換到步驟 1（選擇規格與視覺風格）。',
        actions: [{ type: 'set-gen-step', step: 1, description: '切換到步驟 1' }],
      };
    }
    if (contains(userInput, '步驟二', '第二步', '步驟2', '填內容', '內容頁')) {
      return {
        text: '已幫您切換到步驟 2（上傳資料與填寫內容）。',
        actions: [{ type: 'set-gen-step', step: 2, description: '切換到步驟 2' }],
      };
    }
    if (contains(userInput, '步驟三', '第三步', '步驟3', '生成', '開始生成', '模式選擇')) {
      return {
        text: '已幫您切換到步驟 3（選擇生成模式）。接下來請選擇模式一、二或三，然後點擊「開始生成」按鈕！',
        actions: [
          { type: 'set-gen-step', step: 3, description: '切換到步驟 3' },
          { type: 'hint', message: '請選擇生成模式後，點擊「開始生成」按鈕', description: '提示點擊生成按鈕' },
        ],
      };
    }

    // 怎麼開始
    if (contains(userInput, '怎麼', '如何', '怎樣') && contains(userInput, '開始', '生成', '做', '使用')) {
      return {
        text: `快速生成的流程分三步驟：\n\n**步驟 1**：選擇圖片規格（A4、IG、FB等）和視覺風格\n**步驟 2**：上傳素材圖片並填寫活動內容\n**步驟 3**：選擇生成模式，點擊「開始生成」\n\n您目前在步驟 ${context.quickGen?.step ?? 1}，需要我幫您切換步驟或填寫內容嗎？`,
        actions: [],
      };
    }
  }

  // ── 系統設定頁 ─────────────────────────────────────────────────────────────

  if (context.pageId === 'settings') {
    if (contains(userInput, '里名', '里別', '村名', '組織', '單位', '店名') && contains(userInput, '改', '改成', '設定', '變更', '更改', '叫做')) {
      const match = userInput.match(/改(?:成|為|叫做?)?\s*([^\s,，。！？]+)/);
      const newName = match ? match[1] : null;
      if (newName) {
        return {
          text: `已幫您將組織名稱更新為「${newName}」。記得點擊「儲存變更」按鈕才會正式儲存！`,
          actions: [
            { type: 'set-global-setting', payload: { organizationName: newName }, description: `更新組織名稱為 ${newName}` },
            { type: 'hint', message: '請點擊「儲存變更」按鈕完成儲存', description: '提示儲存' },
          ],
        };
      }
      return {
        text: '請告訴我要改成什麼名稱？（例如：「幫我把組織名稱改成信義里」）',
        actions: [],
      };
    }

    if (contains(userInput, '姓名', '名字') && contains(userInput, '改', '改成', '設定', '變更', '更改', '叫做')) {
      const match = userInput.match(/(?:改(?:成|為|叫做?)?|名字是|叫做?)\s*([^\s,，。！？]{2,5})/);
      const newName = match ? match[1] : null;
      if (newName && !contains(newName, '改', '設定', '變更')) {
        return {
          text: `已幫您將姓名更新為「${newName}」。記得點擊「儲存變更」按鈕才會正式儲存！`,
          actions: [
            { type: 'set-global-setting', payload: { name: newName }, description: `更新姓名為 ${newName}` },
            { type: 'hint', message: '請點擊「儲存變更」按鈕完成儲存', description: '提示儲存' },
          ],
        };
      }
      return {
        text: '請告訴我要改成什麼姓名？（例如：「幫我把姓名改成李小花」）',
        actions: [],
      };
    }

    if (contains(userInput, '頭像', '照片', '大頭照', '形象照', '上傳')) {
      return {
        text: '請點擊個人設定區塊中的「選擇圖片」按鈕，或直接點擊頭像圖示來上傳您的形象照。支援 JPG、PNG 格式，最大 5MB。',
        actions: [{ type: 'hint', message: '請點擊頭像圖示或「選擇圖片」按鈕上傳', description: '提示上傳頭像' }],
      };
    }
  }

  // ── 風格轉移頁 ─────────────────────────────────────────────────────────────

  if (context.pageId === 'style-transfer') {
    const styleMap: Record<string, string> = {
      '動漫': 'anime', '動畫': 'anime', 'anime': 'anime',
      '賽博龐克': 'cyberpunk', 'cyberpunk': 'cyberpunk', '未來': 'cyberpunk',
      '水彩': 'watercolor', 'watercolor': 'watercolor',
      '油畫': 'oilpainting', 'oilpainting': 'oilpainting',
      '3d': '3d', '3D': '3d', '立體': '3d',
      '素描': 'sketch', 'sketch': 'sketch',
      '像素': 'pixel', 'pixel': 'pixel', '馬賽克': 'pixel',
      '科幻': 'futuristic', 'futuristic': 'futuristic',
    };

    for (const [keyword, styleId] of Object.entries(styleMap)) {
      if (contains(userInput, keyword)) {
        return {
          text: `已幫您選擇「${keyword}」風格。上傳圖片後點擊「開始生成」按鈕即可！`,
          actions: [
            {
              type: 'dispatch-event',
              eventName: 'assistant:style-transfer:select-style',
              detail: { styleId },
              description: `選擇 ${keyword} 風格`,
            },
          ],
        };
      }
    }

    if (contains(userInput, '上傳', '怎麼上傳', '如何上傳')) {
      return {
        text: '您可以將圖片拖曳到上傳區域，或點擊上傳區域選擇圖片檔案（支援 JPG、PNG，最大 10MB）。',
        actions: [{ type: 'hint', message: '請拖曳或點擊上傳區域選擇圖片', description: '提示上傳圖片' }],
      };
    }

    if (contains(userInput, '怎麼', '如何') && contains(userInput, '生成', '開始', '轉換')) {
      return {
        text: '風格轉移的步驟：\n\n1. 上傳您的原始圖片\n2. 選擇目標風格（如動漫、水彩、油畫等）\n3. 點擊「開始生成」按鈕\n\n需要我幫您選擇風格嗎？',
        actions: [],
      };
    }
  }

  // ── 工具包頁 ───────────────────────────────────────────────────────────────

  if (context.pageId === 'toolkit') {
    const categoryMap: Record<string, string> = {
      '政績': '政績',
      '活動': '活動',
      'line': 'LINE選單',
      'LINE': 'LINE選單',
      '選單': 'LINE選單',
      '節氣': '節氣',
      '全部': '全部',
    };

    for (const [keyword, category] of Object.entries(categoryMap)) {
      if (contains(userInput, keyword)) {
        return {
          text: `已幫您篩選「${category}」分類的工具包範本。`,
          actions: [
            {
              type: 'dispatch-event',
              eventName: 'assistant:toolkit:filter-category',
              detail: { category },
              description: `篩選 ${category} 分類`,
            },
          ],
        };
      }
    }
  }

  // ── 預設：無法理解時的回覆 ─────────────────────────────────────────────────

  const hints: Record<string, string> = {
    'quick-gen': '您可以說「幫我填健康講座內容」、「選A4規格」、「切到步驟2」等指令。',
    'settings': '您可以說「把組織名稱改成XX」、「把姓名改成XXX」等指令。',
    'style-transfer': '您可以說「幫我選動漫風格」、「怎麼上傳圖片」等指令。',
    'toolkit': '您可以說「看活動類工具包」、「篩選政績類」等指令。',
    'ai-collab': '您可以在右側的 AI 對話面板直接與 AI 溝通，設計您的文宣作品。',
    'history': '生成紀錄頁面可查看所有過去的生成結果，包含使用的設定和生成圖片。',
  };

  return {
    text: `抱歉，我目前無法理解您的指令。您目前在「${context.pageLabel}」頁面。\n\n${hints[context.pageId] ?? '請嘗試更具體的描述，例如「帶我去快速生成」或「功能說明」。'}`,
    actions: [],
  };
}
